import { fanduel } from "@fanduel/formation-tokens";
import { SortDirection } from "@tanstack/react-table";
import cx from "classnames";
import { CSSProperties, PropsWithChildren, forwardRef } from "react";
import styles from "./tableStyles.module.scss";

type SortOnClickHandler = ((event: unknown) => void) | undefined;

type TableContainerProps = {
  showExpand: boolean;
  showFooter: boolean;
};

type VthProps = {
  style?: CSSProperties;
  colSpan: number;
  isSorted: boolean | SortDirection;
  onClick: SortOnClickHandler;
};

type VtdProps = {
  isSorted: boolean | SortDirection;
};

// handle table rounded corners
export const Table = ({
  showExpand,
  showFooter,
  ...props
}: PropsWithChildren<TableContainerProps>) => (
  <table
    className={cx(styles.table, {
      [styles.showExpand]: showExpand,
      [styles.showFooter]: showFooter,
    })}
    {...props}
  />
);

export const Tr = (props: PropsWithChildren) => {
  return <tr className={styles.tr} {...props} />;
};

export const Th = (
  props: PropsWithChildren<{ onClick: SortOnClickHandler }>
) => {
  return <th className={styles.th} {...props} />;
};

export const Tbody = (props: PropsWithChildren) => {
  return <tbody className={styles.tbody} {...props} />;
};

export const Td = (props: PropsWithChildren) => {
  return <td className={styles.td} {...props} />;
};

export const Tfoot = (props: PropsWithChildren) => {
  return <tfoot className={styles.tfoot} {...props} />;
};

export const ThContent = (props: PropsWithChildren) => {
  return <div className={styles.thcontent} {...props} />;
};

export const SortContainer = (props: PropsWithChildren) => {
  return <div className={styles.sortContainer} {...props} />;
};

export const TableContainer = ({
  showExpand,
  ...props
}: PropsWithChildren<{ showExpand: boolean }>) => (
  <div
    className={cx(styles.tableContainer, { [styles.expanded]: showExpand })}
    {...props}
  />
);

// TableVirtualized Styles
export const Vtable = (props: PropsWithChildren<{ style?: CSSProperties }>) => (
  <table className={styles.vtable} {...props} />
);

/**
 * must use row index instead of nth-of-type to style alternating rows
 * https://github.com/petyosi/react-virtuoso/issues/15
 */
export const Vtr = ({
  index,
  style,
  ...props
}: PropsWithChildren<{ index: number; style?: CSSProperties }>) => {
  return (
    <tr
      style={{
        ...style,
        background:
          index % 2 === 0
            ? fanduel.colors.backgroundSurface
            : fanduel.colors.backgroundLayer,
      }}
      {...props}
    />
  );
};

export const Vtbody = forwardRef<
  HTMLTableSectionElement,
  PropsWithChildren<{ className?: string }>
>((props, ref) => (
  <tbody ref={ref} {...props} className={cx(styles.vtbody, props.className)} />
));
Vtbody.displayName = "Vtbody";

export const VHeader = (props: PropsWithChildren) => (
  <div className={styles.vheader} {...props} />
);

export const Vth = ({
  isSorted,
  style,
  ...props
}: PropsWithChildren<VthProps>) => {
  let styleOverrides;
  if (isSorted) {
    styleOverrides = { backgroundColor: fanduel.colors.coreGreyL3 };
  }

  return <th style={{ ...style, ...styleOverrides }} {...props} />;
};

export const Vtd = ({ isSorted, ...props }: PropsWithChildren<VtdProps>) => (
  <td
    className={cx(styles.vtd, {
      [styles.sorted]: isSorted,
    })}
    {...props}
  />
);
